import { ApiError } from '@commissionsight/sdk';

/**
 * Statement-first carrier setup — app-only endpoints (not in the public SDK), so
 * the web app calls them directly with the session token: global detection,
 * "add my carrier" requests, and the account's request list.
 */

export interface DetectedCarrier {
  id: string;
  name: string | null;
  slug: string | null;
  hasActiveConfig: boolean;
}

export interface DetectResponse {
  matchKind: 'signature' | 'scored' | 'none';
  carrier: DetectedCarrier | null;
  signatureStatus: 'verified' | 'candidate' | null;
  ambiguous: boolean;
  ranked: { carrierId: string; confidence: number; reason: string; name: string | null }[];
  inferredProductLine: string;
}

export interface CarrierRequestRow {
  id: string;
  requestedName: string;
  status: 'pending' | 'approved' | 'merged' | 'rejected';
  resolvedCarrierName: string | null;
  createdAt: string;
  decidedAt: string | null;
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'title' in body
        ? String((body as { title: unknown }).title)
        : res.statusText;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

export async function detectStatement(
  baseUrl: string,
  token: string | null,
  file: File,
): Promise<DetectResponse> {
  const form = new FormData();
  form.set('file', file);
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/carriers/detect`, {
    method: 'POST',
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  return parseOrThrow<DetectResponse>(res);
}

export async function requestCarrier(
  baseUrl: string,
  token: string | null,
  input: { file: File; name: string; note?: string },
): Promise<{ id: string; status: string; requestedName: string }> {
  const form = new FormData();
  form.set('file', input.file);
  form.set('name', input.name);
  if (input.note) form.set('note', input.note);
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/carriers/requests`, {
    method: 'POST',
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
    body: form,
  });
  return parseOrThrow(res);
}

export async function listCarrierRequests(
  baseUrl: string,
  token: string | null,
): Promise<CarrierRequestRow[]> {
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/carriers/requests`, {
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
  });
  const body = await parseOrThrow<{ data: CarrierRequestRow[] }>(res);
  return body.data;
}
